/**
 * Hybrid live editing in CodeMirror (decisions 2, 26): table D's decorations for what is on screen, rebuilt when the
 * text, the screen or the selection changes, and the escapes and entities the cursor treats as one character.
 */
import { EditorSelection, RangeSet } from '@codemirror/state';
import type { EditorState, Extension, Range, Text } from '@codemirror/state';
import { Decoration, EditorView, ViewPlugin, WidgetType } from '@codemirror/view';
import type { DecorationSet, PluginValue, ViewUpdate } from '@codemirror/view';
import { chapterField } from './chapter-state.ts';
import { liveClipboard, pageClipboard } from './copy.ts';
import type { ClipboardMemory } from './copy.ts';
import { LIVE_CLASS, liveDecorations } from './live.ts';
import type { LiveDecoration, LiveView, SourceRange } from './live.ts';

/** Text shown in place of a range: an entity's character, a bullet or a scene-break ornament. */
export class TextWidget extends WidgetType {
  readonly text: string;
  readonly className: string;

  constructor(text: string, className: string) {
    super();
    this.text = text;
    this.className = className;
  }

  override eq(other: TextWidget): boolean {
    return other.text === this.text && other.className === this.className;
  }

  /**
   * Fix-next 19: CodeMirror handles a click on it, as on the text around it. Left to the browser (CodeMirror's default),
   * a click put the browser's own caret in this span, which the cursor's touch then replaced with the markup (D13): the
   * typing after went nowhere (Chromium, WebKit) or to the chapter's start (Firefox).
   */
  override ignoreEvent(): boolean {
    return false;
  }

  /** A span holding the text as a text node: nothing is parsed as HTML (decision 44). */
  toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = this.className;
    span.textContent = this.text;
    return span;
  }
}

/**
 * Table SB: where text goes after the block markup a widget at `at` stands for: a scene break's end, or past a bullet's
 * mark and the blanks after it (the item's content). The node starting there is the widget's own: D12 and D13 draw
 * these widgets over a `SceneBreak` or a `ListMark`, whole.
 */
function afterBlockMark(state: EditorState, at: number): number {
  const node = state.field(chapterField).tree.resolveInner(at, 1);
  if (node.name === 'SceneBreak') return node.to;
  const line = state.doc.lineAt(node.to);
  let to = node.to;
  while (to < line.to && /[ \t]/u.test(state.doc.sliceString(to, to + 1))) to += 1;
  return to;
}

/** Widgets that stand for a block's own markup, whose content (if any) comes after them: the ornament, a bullet. */
const BLOCK_WIDGETS = [LIVE_CLASS.sceneBreak, LIVE_CLASS.bullet];

/**
 * Fix-next 19, table SB: a press on a scene break's ornament or a bullet puts the cursor after it, where the writer's
 * text goes: the break's end (typing there makes a paragraph after it) or the item's content. CodeMirror's own mapping
 * gave the widget's start, before markup the press then shows (D12, D13), so the next text wrote over the break (`x***`)
 * or before the item's mark (`x- item`). A drag from it selects from there; any other press is CodeMirror's.
 */
const blockWidgetPress = EditorView.mouseSelectionStyle.of((view, event) => {
  if (event.button !== 0 || event.detail !== 1 || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return null;
  const target = event.target instanceof Element ? event.target.closest(BLOCK_WIDGETS.map((name) => `span.${name}`).join(',')) : null;
  if (target === null || !view.contentDOM.contains(target)) return null;
  const after = afterBlockMark(view.state, view.posAtDOM(target, 0));
  return {
    get: (current) => {
      const at = view.posAtCoords({ x: current.clientX, y: current.clientY });
      const inside = target.contains(current.target instanceof Node ? current.target : null);
      return inside || at === null ? EditorSelection.single(after) : EditorSelection.single(after, at);
    },
    update: () => false,
  };
});

const HIDE = Decoration.replace({});
/** A marker for atomic ranges; only its ranges matter. */
const ATOMIC = Decoration.mark({});

/** One decoration as CodeMirror's. */
export function toDecoration(decoration: LiveDecoration): Range<Decoration> {
  switch (decoration.kind) {
    case 'hide':
      return HIDE.range(decoration.from, decoration.to);
    case 'mark':
      return Decoration.mark({ class: decoration.className }).range(decoration.from, decoration.to);
    case 'line':
      return Decoration.line({ class: decoration.className }).range(decoration.at);
    case 'widget':
      return Decoration.replace({ widget: new TextWidget(decoration.text, decoration.className) }).range(decoration.from, decoration.to);
  }
}

/** Whether a decoration replaces text across a line end, which CodeMirror refuses from a view plugin. */
function crossesLine(decoration: LiveDecoration, doc: Text): boolean {
  return (decoration.kind === 'hide' || decoration.kind === 'widget') && doc.lineAt(decoration.from).to < decoration.to;
}

/**
 * A built view as CodeMirror's decoration and atomic sets. A replacing decoration across a line end is left out, so
 * a construct table D has no row for yet shows as written instead of making the chapter impossible to open
 * (deep-fix r1; table D has no such row, D24).
 */
export function toSets(live: LiveView, doc: Text): { readonly decorations: DecorationSet; readonly atomic: RangeSet<Decoration> } {
  return {
    decorations: Decoration.set(live.decorations.filter((d) => !crossesLine(d, doc)).map(toDecoration), true),
    atomic: RangeSet.of(live.atomic.map((range) => ATOMIC.range(range.from, range.to)), true),
  };
}

/** What is on screen: from the first visible range's start to the last one's end. */
export function screenRange(ranges: readonly SourceRange[]): SourceRange {
  return { from: ranges[0]?.from ?? 0, to: ranges.at(-1)?.to ?? 0 };
}

class LivePlugin implements PluginValue {
  decorations: DecorationSet;
  atomic: RangeSet<Decoration>;

  constructor(view: EditorView) {
    ({ decorations: this.decorations, atomic: this.atomic } = LivePlugin.build(view));
  }

  update(update: ViewUpdate): void {
    if (update.docChanged || update.viewportChanged || update.selectionSet) {
      ({ decorations: this.decorations, atomic: this.atomic } = LivePlugin.build(update.view));
    }
  }

  private static build(view: EditorView): ReturnType<typeof toSets> {
    const { state } = view;
    return toSets(liveDecorations(state.field(chapterField).tree, state.doc, screenRange(view.visibleRanges), state.selection.ranges), state.doc);
  }
}

/** The plugin that builds table D for what is on screen. */
export const livePlugin: ViewPlugin<LivePlugin> = ViewPlugin.fromClass(LivePlugin, { decorations: (plugin) => plugin.decorations });

/** The plugin's atomic ranges; none while it is missing (CodeMirror drops a plugin that throws). */
export const liveAtomicRanges: Extension = EditorView.atomicRanges.of((view) => view.plugin(livePlugin)?.atomic ?? RangeSet.empty);

/**
 * The editor's view of a chapter: the parse, table D's decorations and atomic ranges, and the clipboard (C) with the
 * page's memory, or `memory`.
 */
export function liveView(memory: ClipboardMemory = pageClipboard): Extension {
  return [chapterField, livePlugin, liveAtomicRanges, liveClipboard(memory), blockWidgetPress];
}
